import { useTheme } from '@emotion/react';
import { MouseEvent, useEffect, useRef } from 'react';
import { Image } from '../picture/picture';
import { Link } from '../styled/Link';
import { DEVICE_SCREENS_MAX_STRING } from '../../lib/consts';
import ArrowSVG from '../../public/img/angle-right.svg';
import { PersonInListV3 } from '../../models/person';
import userNoPhotoImg from '../../public/img/user-no-photo.png';

export function PeopleSlider({
  personList,
  isShowArrows = true,
}: {
  personList: PersonInListV3[];
  isShowArrows?: boolean;
}) {
  const slider = useRef<HTMLDivElement | null>(null);
  const items = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  let pressed = false;
  let clientX = 0;
  let scrollX = 0;
  let rightMax = 0;

  const automaticSwitch = () => {
    if (slider?.current) {
      const coefficient = Math.round(Math.abs(scrollX / 180));
      slider.current.scrollTo({ left: 180 * coefficient, behavior: 'smooth' });
      scrollX = -(180 * coefficient);
    }
  };

  const onMouseDown = (e: MouseEvent) => {
    e.preventDefault();

    pressed = true;
    clientX = e.clientX;
  };

  const onMouseUp = (e: MouseEvent) => {
    e.preventDefault();

    if (items.current) {
      items.current.style.pointerEvents = 'unset';
    }
    pressed = false;
    automaticSwitch();
  };

  const onMouseMove = (e: MouseEvent) => {
    e.preventDefault();

    if (pressed && items.current && slider.current) {
      items.current.style.pointerEvents = 'none';

      let sX = scrollX + e.clientX - clientX;
      const cX = e.clientX;

      slider.current.scrollLeft = -sX;

      if (!rightMax) {
        rightMax = 180 * personList.length - slider.current.clientWidth;
      }

      if (rightMax < -sX) {
        sX = -rightMax;
      } else if (-sX < 0) {
        sX = 0;
      }

      scrollX = sX;
      clientX = cX;
    }
  };

  const slideLeft = () => {
    if (!slider.current) return;
    if (!rightMax) {
      rightMax = 180 * personList.length - slider.current.clientWidth;
    }
    if (scrollX < 0) {
      scrollX = -scrollX - 180 < 180 ? 0 : scrollX + 180;
      slider.current.scrollTo({ left: -scrollX, behavior: 'smooth' });
    }
  };

  const slideRight = () => {
    if (!slider.current) return;
    if (!rightMax) {
      rightMax = 180 * personList.length - slider.current.clientWidth;
    }
    if (rightMax > -scrollX) {
      scrollX = rightMax + scrollX < 180 ? -rightMax : scrollX - 180;
      slider.current.scrollTo({ left: -scrollX, behavior: 'smooth' });
    }
  };

  const onResize = () => {
    if (slider.current) {
      rightMax = 180 * personList.length - slider.current.clientWidth;
    }
  };

  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div css={{ display: 'flex', alignItems: 'center' }}>
      {isShowArrows && (
        <button
          css={{
            width: '3%',
            [DEVICE_SCREENS_MAX_STRING.md]: { display: 'none' },
          }}
          type='button'
          onClick={slideLeft}
        >
          <ArrowSVG
            css={{
              transform: 'rotate(180deg)',
              width: 10,
              height: 16,
              fill: theme.colors.primary,
            }}
          />
        </button>
      )}
      <div
        ref={slider}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        onMouseLeave={() => {
          pressed = false;
        }}
        css={{
          width: '100%',
          overflowX: 'scroll',
          display: 'flex',
          gap: 30,
          scrollbarWidth: 'none',
          '-ms-overflow-style': 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <div ref={items} css={{ display: 'flex', gap: 30 }}>
          {personList.map((person) => (
            <Link
              href={['', 'person', person.slug, ''].join('/')}
              key={`People-slider-person-${person.id}`}
            >
              <div
                css={{
                  position: 'relative',
                  width: 150,
                  height: 150,
                  borderRadius: 150,
                  overflow: 'hidden',
                  objectFit: 'cover',
                  pointerEvents: 'none',
                }}
              >
                <Image
                  css={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  is360
                  image={
                    person.first_image?.image
                      ? person.first_image
                      : userNoPhotoImg.src
                  }
                  alt={`${person.first_name} ${person.last_name}`}
                />
              </div>
              <div css={{ color: theme.colors.red, textAlign: 'center' }}>
                {person.first_name} {person.last_name}
              </div>
            </Link>
          ))}
        </div>
      </div>
      {isShowArrows && (
        <button
          css={{
            width: '3%',
            [DEVICE_SCREENS_MAX_STRING.md]: { display: 'none' },
          }}
          type='button'
          onClick={slideRight}
        >
          <ArrowSVG
            css={{ width: 10, height: 16, fill: theme.colors.primary }}
          />
        </button>
      )}
    </div>
  );
}
